// src/components/kb/KbUploadForm.tsx
//
// Upload overlay for the Knowledge Base screen (Story 2.1). File picker
// (.pdf/.md/.txt with a client-side size preflight), editable title
// (pre-filled from the filename), optional brand (free text + datalist of
// brands already in use), and a rep-shareable toggle defaulting OFF. Ingest
// takes seconds (extract → chunk → embed edge-side), so the submit button
// shows a pending "Processing…" state; failures render an inline retryable
// banner. No optimistic updates — the row appears only from the server's
// answer (the parent refetches on success). Esc closes; focus returns to the
// trigger on close (stable fallback when the trigger unmounted).

import React, { useEffect, useId, useRef, useState } from 'react'
import { Upload, X } from 'lucide-react'
import { Button } from '../ui/Button'
import type { KbUploadInput } from '../../services/kbDocumentsService'

// Mirror the edge function's bounds so we fail fast client-side before a
// pointless upload (the edge re-validates — it is the real boundary).
const ACCEPT = '.pdf,.md,.txt'
const MAX_FILE_BYTES = 10 * 1024 * 1024

interface KbUploadFormProps {
  brandsInUse: string[]
  onUpload: (file: File, input: KbUploadInput) => Promise<void>
  onClose: () => void
}

function deriveTitle(filename: string): string {
  const dot = filename.lastIndexOf('.')
  return dot === -1 ? filename : filename.slice(0, dot)
}

export function KbUploadForm({
  brandsInUse,
  onUpload,
  onClose,
}: KbUploadFormProps) {
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [brand, setBrand] = useState('')
  const [repShareable, setRepShareable] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const titleErrorId = useId()
  const fileErrorId = useId()
  const brandListId = useId()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Focus the file picker on open (the natural first field).
  useEffect(() => {
    fileInputRef.current?.focus()
  }, [])

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const picked = e.target.files?.[0] ?? null
    if (!picked) {
      setFile(null)
      return
    }
    if (picked.size > MAX_FILE_BYTES) {
      setFile(null)
      setError('File is too large. The limit is 10 MB.')
      return
    }
    setFile(picked)
    // Pre-fill the title from the filename only if the user hasn't typed one.
    setTitle(prev => (prev.trim() ? prev : deriveTitle(picked.name)))
  }

  const canSubmit = !!file && !!title.trim() && !pending

  const submit = async () => {
    if (!canSubmit || !file) return
    setPending(true)
    setError(null)
    try {
      await onUpload(file, {
        title: title.trim(),
        brand: brand.trim() || null,
        repShareable,
      })
      onClose()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "The document couldn't be processed. Nothing was saved — try again."
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape' && !pending) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kb-upload-title"
        className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6"
      >
        <div className="flex items-start justify-between mb-1">
          <h3
            id="kb-upload-title"
            className="text-lg font-semibold text-gray-900"
          >
            Upload document
          </h3>
          <button
            type="button"
            onClick={() => !pending && onClose()}
            disabled={pending}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Yak answers from active documents only. PDF, Markdown or plain text,
          up to 10 MB.
        </p>

        {error && (
          <div
            role="alert"
            className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
          >
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="form-group">
            <label htmlFor="kb-file" className="form-label">
              File
            </label>
            <input
              ref={fileInputRef}
              id="kb-file"
              type="file"
              accept={ACCEPT}
              disabled={pending}
              aria-describedby={error ? fileErrorId : undefined}
              onChange={onFileChange}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-gray-300 file:text-sm file:bg-white hover:file:bg-gray-50 disabled:opacity-50"
            />
            <span id={fileErrorId} className="sr-only">
              Accepted types: PDF, Markdown, plain text. Maximum 10 MB.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="kb-title" className="form-label">
              Title
            </label>
            <input
              id="kb-title"
              type="text"
              className="input"
              value={title}
              disabled={pending}
              placeholder="Document title"
              aria-describedby={titleErrorId}
              onChange={e => setTitle(e.target.value)}
            />
            <span id={titleErrorId} className="form-help">
              The display name, editable later. Defaults to the filename.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="kb-brand" className="form-label">
              Brand tag <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="kb-brand"
              type="text"
              className="input"
              list={brandListId}
              value={brand}
              disabled={pending}
              placeholder="Leave blank for all brands"
              onChange={e => setBrand(e.target.value)}
            />
            <datalist id={brandListId}>
              {brandsInUse.map(b => (
                <option key={b} value={b} />
              ))}
            </datalist>
            <span className="form-help">
              Untagged documents are searchable regardless of brand.
            </span>
          </div>

          <div className="flex items-start gap-2">
            <input
              id="kb-rep-shareable"
              type="checkbox"
              checked={repShareable}
              disabled={pending}
              onChange={e => setRepShareable(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="kb-rep-shareable" className="text-sm text-gray-700">
              Rep-shareable
              <span className="block text-gray-500">
                Reps can receive links to this document in recap emails.
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={() => onClose()} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={pending} disabled={!canSubmit}>
            {!pending && <Upload className="w-4 h-4 mr-1" />}
            {pending ? 'Processing…' : 'Upload document'}
          </Button>
        </div>
      </div>
    </div>
  )
}
